import {
  createMenusReviewClient,
  type Category as ClientCategory,
  type MenuItem as ClientMenuItem,
  type MenusReviewClient,
  type ReviewItem as ClientReviewItem,
} from '@hurdl/uk-fast-food-menus-backend/client';

import {
  type AllergenPresence,
  type AllergenValue,
  type AllergenView,
  type Category,
  type FieldChange,
  type FieldValue,
  type MenuWorkspaceOperations,
  type Nutrition,
  type ReviewAllergens,
  type ReviewedItem,
  type ReviewItem,
  type ReviewSubmission,
} from './types.js';

/**
 * Wires the menu-workspace Bespoke_Page to the real Menus_Backend surface through its
 * Generated_Client.
 *
 * The panel composes the client and maps it onto the {@link MenuWorkspaceOperations}
 * seam the component takes as a prop: the component holds no request, no envelope read
 * and no error map — those are the client's — and this module holds only the mapping
 * between the backend's record shapes and the views the page renders, the operator the
 * writes are attributed to, and the sequencing that turns one Operator submission into
 * the granular backend operations.
 *
 * **No address literal and no `fetch` reaches these sources** (admin-panel R13.6): the
 * base address is the product-scoped path the panel mounts the Bespoke_Page router at —
 * a relative path assembled from the Product key, never an absolute URL — and the
 * request is the client's. The admin-panel backend mounts the router at
 * `/products/<productKey>/bespoke/<pageKey>`, which is the base the client resolves each
 * operation against.
 */

/** What the panel supplies to compose the menu-workspace operations. */
export interface MenuWorkspaceWiring {
  /** The Product the Bespoke_Page belongs to, naming the mount path. */
  readonly productKey: string;

  /** The Bespoke_Page key, naming the mount path's last segment. */
  readonly pageKey: string;

  /**
   * The operator the writes are attributed to.
   *
   * A full build resolves the acting account through the composed
   * Authorisation_Capability; this build supplies a named operator, and the
   * Menus_Backend's own Permission_Check is what a real deployment gates with.
   */
  readonly operator: string;

  /**
   * A function performing an authenticated request, where the panel composes one.
   *
   * Absent in this build — authentication is the full Admin_Panel programme's to
   * compose — so requests go through the browser's own `fetch` inside the client, which
   * the Menus_Backend admits under its local-development opt-in and refuses otherwise.
   */
  readonly authorisedFetch?: typeof globalThis.fetch;
}

/** The relative base the Bespoke_Page router is mounted at — never an absolute URL. */
function mountPath(productKey: string, pageKey: string): string {
  return `/products/${productKey}/bespoke/${pageKey}`;
}

/** The allergens a reviewer reads, in a reader's order (the platform's set). */
const ALLERGEN_ORDER = [
  'wheat',
  'rye',
  'barley',
  'oats',
  'spelt',
  'kamut',
  'soya',
  'nuts',
  'peanuts',
  'sesame',
  'milk',
  'eggs',
  'fish',
  'crustaceans',
  'celery',
  'sulphurDioxideAndSulphites',
  'mustard',
  'molluscs',
  'lupin',
] as const;

/** Maps a backend allergen map (presence or absent) to the review view's presence map. */
function toReviewAllergens(
  source: Readonly<Record<string, unknown>> | undefined,
): ReviewAllergens {
  if (source === undefined) return {};

  const out: Record<string, AllergenPresence> = {};
  for (const [key, value] of Object.entries(source)) {
    // The two suitability flags are booleans, not presence values; the before/after is
    // over the presence-valued allergens alone.
    if (value === 'contains' || value === 'may-contain' || value === 'none') {
      out[key] = value;
    }
  }
  return out;
}

/** Maps a backend nutrition map to the view's nutrition map. */
function toNutrition(
  source: Readonly<Record<string, unknown>> | undefined,
): Nutrition {
  if (source === undefined) return {};

  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'number') out[key] = value;
  }
  return out;
}

/** Whether a value is one of the four allergen presence values the editor shows. */
function isAllergenValue(value: unknown): value is AllergenValue {
  return (
    value === 'contains' ||
    value === 'may-contain' ||
    value === 'none' ||
    value === 'absent'
  );
}

/** Maps a client field value (allergen/nutrient/text) onto the view's tagged value. */
function toFieldValue(value: unknown): FieldValue | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const v = value as Record<string, unknown>;
  if (v.kind === 'allergen' && typeof v.presence === 'string') {
    return { kind: 'allergen', presence: v.presence as AllergenPresence };
  }
  if (v.kind === 'nutrient' && typeof v.amount === 'number') {
    return { kind: 'nutrient', amount: v.amount };
  }
  if (v.kind === 'text' && typeof v.text === 'string') {
    return { kind: 'text', text: v.text };
  }
  return undefined;
}

/** Maps one client Field_Change onto the view's. */
function toFieldChange(
  change: ClientReviewItem['changes'][number],
): FieldChange {
  const acquired = toFieldValue(change.acquired) ?? { kind: 'text', text: '' };
  const held = toFieldValue(change.held);
  return {
    field: change.field,
    ...(held === undefined ? {} : { held }),
    acquired,
    resolution: change.resolution,
  };
}

/** Maps one client Review_Item onto the review view the page renders. */
function toReviewItem(item: ClientReviewItem): ReviewItem {
  return {
    itemKey: item.itemKey,
    restaurantKey: item.restaurantKey,
    reason: item.reason,
    extracted: {
      name: item.extracted.extractedName,
      category: item.extracted.extractedCategory,
      allergens: toReviewAllergens(item.extracted.allergens),
      nutrition: toNutrition(item.extracted.nutrition),
    },
    changes: item.changes.map(toFieldChange),
    ...(item.imageChange !== undefined
      ? {
          imageChange: {
            ...(item.imageChange.currentImageUrl !== undefined
              ? { currentImageUrl: item.imageChange.currentImageUrl }
              : {}),
            proposedImageUrl: item.imageChange.proposedImage.url,
            resolution: item.imageChange.resolution,
          },
        }
      : {}),
  };
}

/**
 * Maps one published Menu_Item onto the reviewed view, resolving each allergen.
 *
 * The resolved value is the correction held against the allergen where one is held, and
 * the extraction otherwise — the same resolution the snapshot shows. Absent allergens
 * read `absent`, so the editor can set a value where the extraction carried none.
 */
function toReviewedItem(item: ClientMenuItem): ReviewedItem {
  const extracted = (item.allergens ?? {}) as Readonly<Record<string, unknown>>;
  const corrections = (item.allergenCorrections ?? {}) as Readonly<
    Record<string, unknown>
  >;

  const allergens: ReadonlyArray<AllergenView> = ALLERGEN_ORDER.map((key) => {
    const extractedValue = isAllergenValue(extracted[key])
      ? extracted[key]
      : 'absent';
    const correctedValue = isAllergenValue(corrections[key])
      ? corrections[key]
      : extractedValue;
    return { key, extracted: extractedValue, resolved: correctedValue };
  });

  return {
    itemKey: item.itemKey,
    restaurantKey: item.restaurantKey,
    extractedName: item.extractedName,
    extractedCategory: item.extractedCategory,
    ...(item.sizeVariant !== undefined
      ? { sizeVariant: item.sizeVariant }
      : {}),
    ...(item.displayName !== undefined
      ? { displayName: item.displayName }
      : {}),
    ...(item.displayCategory !== undefined
      ? { displayCategory: item.displayCategory }
      : {}),
    ...(item.displaySubcategory !== undefined
      ? { displaySubcategory: item.displaySubcategory }
      : {}),
    ...(item.description !== undefined
      ? { description: item.description }
      : {}),
    ...(item.imageUrl !== undefined ? { imageUrl: item.imageUrl } : {}),
    allergens,
    ...(item.nutrition !== undefined
      ? { nutrition: toNutrition(item.nutrition) }
      : {}),
  };
}

/** Maps one managed Category onto the view the picker offers. */
function toCategory(category: ClientCategory): Category {
  return {
    key: category.key,
    restaurantKey: category.restaurantKey,
    name: category.name,
    ...(category.icon !== undefined ? { icon: category.icon } : {}),
  };
}

/**
 * Builds the {@link MenuWorkspaceOperations} the page composes, backed by the
 * Menus_Backend Generated_Client.
 *
 * `listQueue` answers the unresolved queue alone — a resolved item has left the
 * reviewer's work, so the mapping drops any record carrying a `resolvedAt`.
 * `listReviewed` and `listCategories` read a Restaurant's published items and managed
 * Categories, and `createCategory`/`renameCategory`/`removeCategory` curate that
 * managed set — each a single backend write attributed to the operator.
 * `submitReviewedItem` sequences one submission into the granular writes:
 * the decision first (categorise-and-publish, or confirm/reject), then the display,
 * allergen, nutrition and image edits, each attributed to the operator.
 */
export function createMenuWorkspaceOperations(
  wiring: MenuWorkspaceWiring,
): MenuWorkspaceOperations {
  const client: MenusReviewClient = createMenusReviewClient({
    baseUrl: mountPath(wiring.productKey, wiring.pageKey),
    ...(wiring.authorisedFetch === undefined
      ? {}
      : { authorisedFetch: wiring.authorisedFetch }),
  });

  return {
    listQueue: async () => {
      // Every item in the queue is unresolved: an item is removed once all its changes
      // resolve (R5.5), so there is no resolved-but-present state to filter out.
      const queue = await client.listReviewQueue();
      return queue.items.map(toReviewItem);
    },

    listReviewed: async (restaurantKey) => {
      const items = await client.listMenuItems(restaurantKey);
      return items.items.map(toReviewedItem);
    },

    listCategories: async (restaurantKey) => {
      const categories = await client.listCategories(restaurantKey);
      return categories.categories.map(toCategory);
    },

    createCategory: async (restaurantKey, name) => {
      await client.createCategory({
        restaurant: restaurantKey,
        name,
        actor: wiring.operator,
      });
    },

    renameCategory: async (categoryKey, name) => {
      await client.editCategory(categoryKey, {
        name,
        actor: wiring.operator,
      });
    },

    removeCategory: async (categoryKey) => {
      await client.removeCategory(categoryKey, { actor: wiring.operator });
    },

    submitReviewedItem: async (itemKey, submission: ReviewSubmission) => {
      if (submission.decision?.kind === 'categorise') {
        await client.categoriseNewItem(itemKey, {
          displayCategory: submission.decision.category,
          actor: wiring.operator,
        });
      } else if (submission.decision?.kind === 'change') {
        // A whole-item change decision resolves every field the same way — the blunt
        // path the per-field resolution refines; kept for callers that still send it.
        await client.resolveSignificantChange(itemKey, {
          decision: submission.decision.decision,
          actor: wiring.operator,
        });
      }

      // Per-field resolutions: each accepts or rejects one changed field on its own
      // (R5.2), in the order the operator gave them.
      for (const resolution of submission.fieldResolutions ?? []) {
        await client.resolveReviewField(
          itemKey,
          resolution.field as Parameters<typeof client.resolveReviewField>[1],
          { decision: resolution.decision, actor: wiring.operator },
        );
      }

      // The image resolution, where the operator decided one (R6).
      if (submission.imageResolution !== undefined) {
        await client.resolveReviewImage(itemKey, {
          decision: submission.imageResolution,
          actor: wiring.operator,
        });
      }

      if (submission.display !== undefined) {
        await client.editMenuItemDisplay(itemKey, {
          ...submission.display,
          actor: wiring.operator,
        });
      }

      if (
        submission.corrections !== undefined &&
        submission.corrections.length > 0
      ) {
        await client.correctMenuItemAllergens(itemKey, {
          corrections: submission.corrections.map((correction) => ({
            allergen: correction.allergen as never,
            to: correction.to,
          })),
          actor: wiring.operator,
        });
      }

      if (submission.nutrition !== undefined) {
        await client.setMenuItemNutrition(itemKey, {
          nutrition: submission.nutrition.figures,
          force: submission.nutrition.force,
          actor: wiring.operator,
        });
      }

      if (submission.image !== undefined) {
        await client.uploadMenuItemImage(itemKey, {
          mediaType: submission.image.mediaType,
          contentBase64: submission.image.contentBase64,
          actor: wiring.operator,
        });
      }
    },
  };
}
